import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { PageHeader, Stat, Stats } from '../../components/office';
import { reportsIntelApi } from '@verlo/shared/services/api';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Notice } from '@verlo/shared/components/ui/notice';
import WeekPicker from '../../components/people/WeekPicker';
import { daysOf, mondayOf, ymd } from '../../lib/weeks';
import { useSite } from '../../contexts/SiteContext';

// Labour costs (Back office sweep, Oct 2026). It used to open the P&L overview, because its
// route pointed there. Now it's the team's cost for a week: by day and by role, as a share
// of sales, with the hours and sales per labour hour. Cost is planned hours × rate, plus the
// on-costs when the business counts them, as on the P&L and the Labour report, so the three
// agree.

const money = (v) => (v == null ? '—' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v));
const pct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
const th = 'px-4 py-2 text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint';

export default function LabourCostsPage() {
  const { site } = useSite();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState(null);
  const days = useMemo(() => daysOf(weekStart), [weekStart]);

  useEffect(() => {
    setData(null);
    reportsIntelApi.getLabourCosts(ymd(days[0]), ymd(days[6]), site || undefined)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Couldn't load labour costs"); setData({ error: true }); });
  }, [days, site]);

  const t = data?.totals;
  const maxCost = Math.max(1, ...(data?.by_day || []).map((d) => d.cost));

  return (
    <div className="space-y-5" data-testid="labour-costs-page">
      <PageHeader
        title="Labour costs"
        purpose="What the team costs, by day and by role, against what you sold."
        actions={<WeekPicker weekStart={weekStart} onChange={setWeekStart} testId="labour-week" />}
      />
      {!data ? <Skeleton className="h-72" /> : data.error ? (
        <Notice tone="stop" word="Labour" title="Couldn't load labour costs">Try another week, or come back in a moment.</Notice>
      ) : (
        <>
          <Stats>
            <Stat label="Labour cost" value={money(t.cost)} hint={`Rota hours × each person's rate${t.on_cost_pct ? `, plus ${t.on_cost_pct}${t.on_cost_pct_max > t.on_cost_pct ? `-${t.on_cost_pct_max}` : ''}% on-costs` : ''}`} testId="lc-cost" />
            <Stat label="Labour % of sales" value={pct(t.labour_pct)} hint={t.sales ? `of ${money(t.sales)} sold${t.so_far ? ', the days so far' : ''}` : 'No sales yet'} testId="lc-pct" />
            <Stat label="Hours" value={`${t.hours.toFixed(1)}h`} hint={`${t.worked_hours.toFixed(1)}h worked so far`} testId="lc-hours" />
            <Stat label="Sales per labour hour" value={money(t.splh)} hint={t.so_far ? 'The days so far' : undefined} testId="lc-splh" />
          </Stats>

          <section className="rounded-lg border border-hairline bg-white" data-testid="lc-by-day">
            <h2 className="border-b border-hairline px-5 py-3 font-heading text-[15px] font-extrabold text-ink">By day</h2>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-[13.5px]">
                <thead><tr className="border-b border-hairline"><th className={th}>Day</th><th className={th}>Hours</th><th className={`${th} w-[40%]`}>Cost</th><th className={`${th} text-right`}>Sales</th><th className={`${th} text-right`}>Labour %</th></tr></thead>
                <tbody>
                  {data.by_day.map((d) => (
                    <tr key={d.date} className="border-b border-hairline last:border-0">
                      <td className="px-4 py-2 text-ink">{new Date(`${d.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}</td>
                      <td className="px-4 py-2 font-mono tabular-nums text-ink-soft">{d.hours.toFixed(1)}h</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-3">
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-paper"><div className="h-full bg-verlo" style={{ width: `${(d.cost / maxCost) * 100}%` }} /></div>
                          <span className="w-16 text-right font-mono tabular-nums text-ink">{money(d.cost)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums text-ink-soft">{money(d.sales)}</td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums text-ink">{pct(d.labour_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-lg border border-hairline bg-white" data-testid="lc-by-role">
            <h2 className="border-b border-hairline px-5 py-3 font-heading text-[15px] font-extrabold text-ink">By role</h2>
            {data.by_role.length === 0 ? <p className="px-5 py-6 text-ink-soft">Nobody is on the rota this week.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-[13.5px]">
                  <thead><tr className="border-b border-hairline"><th className={th}>Role</th><th className={th}>People</th><th className={th}>Hours</th><th className={`${th} text-right`}>Cost</th><th className={`${th} text-right`}>Share</th></tr></thead>
                  <tbody>
                    {data.by_role.map((r) => (
                      <tr key={r.role} className="border-b border-hairline last:border-0">
                        <td className="px-4 py-2 text-ink">{r.role}</td>
                        <td className="px-4 py-2 tabular-nums text-ink-soft">{r.people}</td>
                        <td className="px-4 py-2 font-mono tabular-nums text-ink-soft">{r.hours.toFixed(1)}h</td>
                        <td className="px-4 py-2 text-right font-mono tabular-nums text-ink">{money(r.cost)}</td>
                        <td className="px-4 py-2 text-right font-mono tabular-nums text-ink-soft">{r.share_pct.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
